/**
 * Small shared building blocks for the Support module UI.
 *
 * Every colour goes through a private `--sp-*` variable set on a
 * `SupportScope`. The scope maps them onto the host's theme variables with
 * literal fallbacks — so the module follows the platform's light/dark theme,
 * still renders on the downloaded runtime (which defines no platform vars),
 * and can be pinned to light where it sits inside a surface that is always
 * white (the System administration console).
 */

import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'

import type { SupportLifecycle, SupportMeta } from './contract'

const THEMED: Record<string, string> = {
  '--sp-bg': 'var(--color-bg, #ffffff)',
  '--sp-bg2': 'var(--color-bg-secondary, #f8fafc)',
  '--sp-surface': 'var(--color-surface-2, var(--color-bg-secondary, #f1f5f9))',
  '--sp-text': 'var(--color-text, #0f172a)',
  '--sp-muted': 'var(--color-text-secondary, #64748b)',
  '--sp-border': 'var(--color-border, #e2e8f0)',
  '--sp-accent': 'var(--color-accent, #2563eb)',
  '--sp-error': 'var(--color-error, #dc2626)',
  '--sp-success': 'var(--color-success, #16a34a)',
  '--sp-warning': 'var(--color-warning, #d97706)',
}

const LIGHT: Record<string, string> = {
  '--sp-bg': '#ffffff',
  '--sp-bg2': '#f8fafc',
  '--sp-surface': '#f1f5f9',
  '--sp-text': '#0f172a',
  '--sp-muted': '#64748b',
  '--sp-border': '#e2e8f0',
  '--sp-accent': '#4f46e5',
  '--sp-error': '#dc2626',
  '--sp-success': '#16a34a',
  '--sp-warning': '#d97706',
}

/** The `--sp-*` custom properties, themed or pinned light. */
export function supportVars(light = false): CSSProperties {
  return { ...(light ? LIGHT : THEMED) } as CSSProperties
}

/** Root wrapper every exported Support component renders inside. */
export function SupportScope({ light, style, children }: {
  light?: boolean
  style?: CSSProperties
  children: ReactNode
}) {
  return (
    <div style={{
      ...supportVars(light),
      color: 'var(--sp-text)',
      fontSize: '0.9125rem',
      lineHeight: 1.45,
      ...style,
    }}>
      {/* The spinner's keyframes live here so no host stylesheet is assumed. */}
      <style>{'@keyframes twsp-spin{to{transform:rotate(360deg)}}'}</style>
      {children}
    </div>
  )
}

// ── atoms ──────────────────────────────────────────────────────────────────

export function Spinner({ size = 15 }: { size?: number }) {
  return <Loader2 size={size} style={{ animation: 'twsp-spin 1s linear infinite', flexShrink: 0 }} />
}

export type BtnKind = 'primary' | 'ghost' | 'danger' | 'success' | 'subtle'

export function btn(kind: BtnKind = 'ghost', small = false): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: small ? '0.25rem 0.6rem' : '0.45rem 0.85rem',
    borderRadius: 8, fontWeight: 600, fontSize: small ? '0.75rem' : '0.82rem',
    cursor: 'pointer', border: '1px solid transparent', whiteSpace: 'nowrap',
    lineHeight: 1.3,
  }
  if (kind === 'primary') return { ...base, background: 'var(--sp-accent)', color: '#fff' }
  if (kind === 'success') return { ...base, background: 'var(--sp-success)', color: '#fff' }
  if (kind === 'danger') {
    return { ...base, background: 'transparent', color: 'var(--sp-error)', borderColor: 'var(--sp-error)' }
  }
  if (kind === 'subtle') {
    return { ...base, background: 'var(--sp-surface)', color: 'var(--sp-text)', borderColor: 'var(--sp-border)' }
  }
  return { ...base, background: 'transparent', color: 'var(--sp-text)', borderColor: 'var(--sp-border)' }
}

export const inputStyle: CSSProperties = {
  padding: '0.45rem 0.6rem', border: '1px solid var(--sp-border)', borderRadius: 8,
  fontSize: '0.9025rem', width: '100%', boxSizing: 'border-box',
  color: 'var(--sp-text)', background: 'var(--sp-bg)', fontFamily: 'inherit',
}

export const labelStyle: CSSProperties = {
  display: 'block', fontSize: '0.8125rem', fontWeight: 700, marginBottom: 4,
  color: 'var(--sp-muted)', textTransform: 'uppercase', letterSpacing: '0.03em',
}

export const mutedText: CSSProperties = { color: 'var(--sp-muted)', fontSize: '0.8425rem' }

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'error'

const TONE_VAR: Record<Tone, string> = {
  neutral: 'var(--sp-muted)',
  accent: 'var(--sp-accent)',
  success: 'var(--sp-success)',
  warning: 'var(--sp-warning)',
  error: 'var(--sp-error)',
}

export function Pill({ tone = 'neutral', children, title }: {
  tone?: Tone; children: ReactNode; title?: string
}) {
  const c = TONE_VAR[tone]
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '0.08rem 0.5rem', borderRadius: 999, fontSize: '0.8125rem', fontWeight: 700,
      color: c, border: `1px solid ${c}`, whiteSpace: 'nowrap', lineHeight: 1.5,
    }}>
      {children}
    </span>
  )
}

export function Card({ title, icon, actions, children, style }: {
  title: ReactNode
  icon?: ReactNode
  actions?: ReactNode
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <section style={{
      border: '1px solid var(--sp-border)', borderRadius: 10, background: 'var(--sp-bg)',
      marginBottom: 12, overflow: 'hidden', ...style,
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '0.55rem 0.8rem',
        borderBottom: '1px solid var(--sp-border)', background: 'var(--sp-bg2)',
      }}>
        {icon}
        <strong style={{ fontSize: '0.9025rem' }}>{title}</strong>
        <span style={{ flex: 1 }} />
        {actions}
      </header>
      <div style={{ padding: '0.7rem 0.8rem' }}>{children}</div>
    </section>
  )
}

export function ErrorLine({ message, onRetry }: { message: string; onRetry?: () => void }) {
  if (!message) return null
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.7rem', borderRadius: 8,
      border: '1px solid var(--sp-error)', color: 'var(--sp-error)', fontSize: '0.8625rem',
      margin: '0.4rem 0',
    }}>
      <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{message}</span>
      {onRetry && <button type="button" style={btn('ghost', true)} onClick={onRetry}>Retry</button>}
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{
      padding: '2rem 1rem', textAlign: 'center', color: 'var(--sp-muted)', fontSize: '0.9025rem',
    }}>
      {children}
    </div>
  )
}

/** A tab strip; `tabs` with `show: false` are dropped. */
export function Tabs<T extends string>({ tabs, value, onChange }: {
  tabs: { id: T; label: ReactNode; show?: boolean }[]
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div role="tablist" style={{
      display: 'flex', gap: 4, borderBottom: '1px solid var(--sp-border)', marginBottom: 12,
      flexWrap: 'wrap',
    }}>
      {tabs.filter((t) => t.show !== false).map((t) => {
        const active = t.id === value
        return (
          <button key={t.id} type="button" role="tab" aria-selected={active}
            onClick={() => onChange(t.id)}
            style={{
              padding: '0.5rem 0.85rem', background: 'none', border: 'none', cursor: 'pointer',
              fontWeight: active ? 700 : 500, fontSize: '0.9025rem',
              color: active ? 'var(--sp-accent)' : 'var(--sp-muted)',
              borderBottom: active ? '2px solid var(--sp-accent)' : '2px solid transparent',
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

// ── overlays ───────────────────────────────────────────────────────────────

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

/** A centred dialog, portalled to <body> so no top bar clips it. */
export function SupportModal({ open, onClose, title, width = 560, children, light, zIndex = 100010 }: {
  open: boolean
  onClose: () => void
  title: ReactNode
  width?: number
  children: ReactNode
  light?: boolean
  zIndex?: number
}) {
  useEscape(open, onClose)
  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <div onClick={onClose} data-support-ui="" style={{
      position: 'fixed', inset: 0, zIndex, background: 'rgba(15,23,42,0.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '3.5rem 1rem', overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        style={{ width: '100%', maxWidth: width }}>
        <SupportScope light={light} style={{
          background: 'var(--sp-bg)', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.28)', border: '1px solid var(--sp-border)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '0.9rem 1.1rem',
            borderBottom: '1px solid var(--sp-border)',
          }}>
            <strong style={{ fontSize: '1.0625rem', flex: 1 }}>{title}</strong>
            <button type="button" onClick={onClose} aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sp-muted)', display: 'flex' }}>
              <X size={18} />
            </button>
          </div>
          <div style={{ padding: '1rem 1.1rem' }}>{children}</div>
        </SupportScope>
      </div>
    </div>,
    document.body,
  )
}

/** A right-hand side panel (the same shape as the work panel and the bell). */
export function SupportDrawer({ open, onClose, title, children, width = 560, zIndex = 9400, headerExtra }: {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  width?: number
  zIndex?: number
  headerExtra?: ReactNode
}) {
  useEscape(open, onClose)
  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <div onClick={onClose} data-support-ui="" style={{
      position: 'fixed', inset: 0, zIndex, display: 'flex', justifyContent: 'flex-end',
      background: 'rgba(15,23,42,0.25)',
    }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Support"
        style={{ width: `min(${width}px, 100%)`, height: '100%' }}>
        <SupportScope style={{
          height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--sp-bg)',
          borderLeft: '1px solid var(--sp-border)', boxShadow: '-12px 0 32px rgba(15,23,42,0.14)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '0.85rem 1rem',
            borderBottom: '1px solid var(--sp-border)',
          }}>
            <strong style={{ fontSize: '1.0125rem', flex: 1, minWidth: 0 }}>{title}</strong>
            {headerExtra}
            <button type="button" onClick={onClose} title="Close" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
              border: '1px solid var(--sp-border)', background: 'transparent', color: 'var(--sp-muted)',
            }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0.85rem 1rem' }}>{children}</div>
        </SupportScope>
      </div>
    </div>,
    document.body,
  )
}

// ── helpers ────────────────────────────────────────────────────────────────

/**
 * A readable message from whatever a transport threw — an axios error whose
 * FastAPI body is `{detail: "..."}` or `{detail: {code, message}}`, a fetch
 * wrapper's Error, or a bare string.
 */
export function errorText(err: unknown, fallback = 'Something went wrong.'): string {
  if (!err) return fallback
  if (typeof err === 'string') return err
  const e = err as {
    response?: { data?: unknown; status?: number }
    message?: string
    detail?: unknown
  }
  const data = (e.response?.data ?? e.detail) as unknown
  const fromDetail = (d: unknown): string => {
    if (!d) return ''
    if (typeof d === 'string') return d
    if (Array.isArray(d)) {
      return d.map((x) => (x && typeof x === 'object' && 'msg' in x ? String((x as { msg: unknown }).msg) : String(x)))
        .join('; ')
    }
    if (typeof d === 'object') {
      const o = d as { message?: unknown; detail?: unknown }
      if (typeof o.message === 'string') return o.message
      if (o.detail) return fromDetail(o.detail)
    }
    return ''
  }
  // The downloaded runtime's ApiError carries an axios-style twin whose body
  // is `{error: {message}}` (see runtime/client api/client.ts).
  const envelope = (data as { error?: { message?: unknown } } | null)?.error
  if (envelope && typeof envelope.message === 'string' && envelope.message) return envelope.message
  if (data && typeof data === 'object' && 'detail' in (data as object)) {
    const m = fromDetail((data as { detail: unknown }).detail)
    if (m) return m
  }
  const direct = fromDetail(data)
  if (direct) return direct
  return e.message || fallback
}

export function timeAgo(iso?: string | null): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const mins = Math.round((Date.now() - at.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} d ago`
  return at.toLocaleDateString()
}

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString()
}

/** A state's label, from the lifecycle the server sent. */
export function stateLabel(lifecycle: SupportLifecycle | undefined | null, id?: string | null): string {
  if (!id) return ''
  return lifecycle?.states.find((s) => s.id === id)?.label || id.replace(/_/g, ' ')
}

/** A vocabulary entry's label (issue type, severity) from the policy. */
export function vocabLabel(
  meta: SupportMeta | null | undefined, kind: 'issue_types' | 'severities', id?: string | null,
): string {
  if (!id) return ''
  return meta?.policy?.[kind]?.find((v) => v.id === id)?.label || id.replace(/_/g, ' ')
}

/** Tone for a severity: its POSITION in the policy's list (first = worst),
 *  not its name — a project may rename or add severities. */
export function severityTone(meta: SupportMeta | null | undefined, id?: string | null): Tone {
  const list = meta?.policy?.severities || []
  const i = list.findIndex((s) => s.id === id)
  if (i < 0 || list.length < 2) return 'neutral'
  const rank = i / (list.length - 1)
  return rank <= 0.01 ? 'error' : rank < 0.5 ? 'warning' : rank < 1 ? 'accent' : 'neutral'
}

export function riskTone(risk?: string | null): Tone {
  return risk === 'high' ? 'error' : risk === 'medium' ? 'warning' : risk === 'low' ? 'success' : 'neutral'
}

/** Tone for a status: terminal states are quiet; a failed one is red. */
export function statusTone(lifecycle: SupportLifecycle | undefined | null, id?: string | null): Tone {
  if (!id) return 'neutral'
  if (lifecycle?.terminal?.includes(id)) return 'neutral'
  if (/fail/.test(id)) return 'error'
  const col = lifecycle?.states.find((s) => s.id === id)?.column
  const cols = lifecycle?.columns || []
  if (col && cols.length && cols[cols.length - 1].id === col) return 'success'
  return 'accent'
}

/** Open a Blob in a new tab (attachments on bearer-token surfaces). */
export function openBlob(blob: Blob, filename?: string): void {
  const url = URL.createObjectURL(blob)
  // No 'noopener' here: with it window.open always returns null, and a blob
  // URL of our own making carries no opener risk.
  const w = window.open(url, '_blank')
  if (!w) {
    // A blocked pop-up: fall back to a download so the click still does something.
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'attachment'
    document.body.appendChild(a)
    a.click()
    a.remove()
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
